import axios from 'axios';
import React, { useEffect, useState } from 'react';
import Avatar, { AvatarColorEditor } from '../../components/Avatar';
import { AVATAR_PALETTE, normalizeAvatarPaletteColor } from '../../constants/avatarPalette';
import { API_BASE_URL } from '../../constants';
import { useTranslation } from '../../contexts/TranslationContext';

interface AvatarConfig {
  color1: string;
  color2: string;
  seed: number;
}

interface TipsterResponse {
  full_name?: string | null;
  description?: string;
  tag_1?: string;
  tag_2?: string;
  tag_3?: string;
  avatar_config?: AvatarConfig;
  avatar_matrix?: number[][];
  created_at?: string;
}

const EditProfile: React.FC = () => {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [tag1, setTag1] = useState('');
  const [tag2, setTag2] = useState('');
  const [tag3, setTag3] = useState('');
  const [fullName, setFullName] = useState('');
  const [avatarConfig, setAvatarConfig] = useState<AvatarConfig | null>(null);
  const [createdAt, setCreatedAt] = useState('');
  const [avatarMatrix, setAvatarMatrix] = useState<number[][]>([]);
  const [color1, setColor1] = useState<string>(AVATAR_PALETTE[0]);
  const [color2, setColor2] = useState<string>(AVATAR_PALETTE[1]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTipster = async () => {
      try {
        const response = await axios.get<TipsterResponse>(`${API_BASE_URL}/tipsters/me`, {
          withCredentials: true,
        });
        setFullName(response.data.full_name?.trim() || '');
        setDescription(response.data.description || '');
        setTag1(response.data.tag_1 || '');
        setTag2(response.data.tag_2 || '');
        setTag3(response.data.tag_3 || '');
        const cfg = response.data.avatar_config;
        if (cfg) {
          const c1 = normalizeAvatarPaletteColor(cfg.color1, AVATAR_PALETTE[0]);
          const c2 = normalizeAvatarPaletteColor(cfg.color2, AVATAR_PALETTE[1]);
          setColor1(c1);
          setColor2(c2);
          setAvatarConfig({ ...cfg, color1: c1, color2: c2 });
        } else {
          setAvatarConfig(null);
        }
        setAvatarMatrix(response.data.avatar_matrix || []);
        setCreatedAt(response.data.created_at || '');
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchTipster();
  }, []);

  const formatCreatedAt = (value: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const payload: Record<string, unknown> = {
        description,
        tag_1: tag1,
        tag_2: tag2,
        tag_3: tag3,
      };
      if (avatarConfig) {
        payload.avatar_color1 = color1;
        payload.avatar_color2 = color2;
      }
      const res = await axios.patch<TipsterResponse>(
        `${API_BASE_URL}/tipsters/me`,
        payload,
        { withCredentials: true },
      );
      const cfg = res.data.avatar_config;
      if (cfg) {
        const c1 = normalizeAvatarPaletteColor(cfg.color1, AVATAR_PALETTE[0]);
        const c2 = normalizeAvatarPaletteColor(cfg.color2, AVATAR_PALETTE[1]);
        setColor1(c1);
        setColor2(c2);
        setAvatarConfig({ ...cfg, color1: c1, color2: c2 });
      }
      setAvatarMatrix(res.data.avatar_matrix || []);
      setSuccess(t.expertPanel.saveSuccess);
    } catch (err) {
      setError(t.expertPanel.saveError);
    } finally {
      setSaving(false);
    }
  };

  const colorLabels = {
    color1: t.expertPanel.avatarColor1,
    color2: t.expertPanel.avatarColor2,
    pickColor: t.expertPanel.avatarPickColor,
  };

  if (loading) {
    return (
      <div className="expert-panel">
        <div className="expert-panel__container">
          <div className="expert-panel__loading">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-panel">
      <div className="expert-panel__container">
        <h1 className="expert-panel__title italic-title">{t.expertPanel.editProfile}</h1>
        <div className="expert-panel__card">
          {error && <div className="expert-panel__error">{error}</div>}
          {success && <div className="expert-panel__success">{success}</div>}

          {(fullName || (avatarConfig && avatarMatrix.length > 0)) && (
            <div className="expert-panel__identity">
              {avatarConfig && avatarMatrix.length > 0 && (
                <Avatar matrix={avatarMatrix} color1={color1} color2={color2} />
              )}
              {(fullName || createdAt) && (
                <div className="expert-panel__identity-meta">
                  {fullName && <p className="expert-panel__full-name">{fullName}</p>}
                  {createdAt && (
                    <p className="expert-panel__created-at">
                      {t.expertPanelExtras.accountCreatedOn.replace('{date}', formatCreatedAt(createdAt))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {avatarConfig && (
            <AvatarColorEditor
              color1={color1}
              color2={color2}
              labels={colorLabels}
              onColor1Change={(hex) => {
                setColor1(hex);
                setAvatarConfig((prev) => (prev ? { ...prev, color1: hex } : prev));
              }}
              onColor2Change={(hex) => {
                setColor2(hex);
                setAvatarConfig((prev) => (prev ? { ...prev, color2: hex } : prev));
              }}
            />
          )}

          <div className="expert-panel__field">
            <label htmlFor="description" className="expert-panel__label wide__small__text">
              {t.expertPanel.setDescription}
            </label>
            <textarea
              id="description"
              className="expert-panel__textarea"
              rows={6}
              placeholder={t.expertPanel.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="expert-panel__sublabel">*{t.expertPanel.descriptionSublabel}</div>
          </div>

          <div className="expert-panel__field">
            <label htmlFor="tag1" className="expert-panel__label wide__small__text">
              {t.expertPanelExtras.tag1}
            </label>
            <input
              id="tag1"
              type="text"
              className="expert-panel__input"
              maxLength={20}
              placeholder={t.expertPanelExtras.tagPlaceholderSport}
              value={tag1}
              onChange={(e) => setTag1(e.target.value)}
            />
          </div>

          <div className="expert-panel__field">
            <label htmlFor="tag2" className="expert-panel__label wide__small__text">
              {t.expertPanelExtras.tag2}
            </label>
            <input
              id="tag2"
              type="text"
              className="expert-panel__input"
              maxLength={20}
              placeholder={t.expertPanelExtras.tagPlaceholderLeague}
              value={tag2}
              onChange={(e) => setTag2(e.target.value)}
            />
          </div>

          <div className="expert-panel__field">
            <label htmlFor="tag3" className="expert-panel__label wide__small__text">
              {t.expertPanelExtras.tag3}
            </label>
            <input
              id="tag3"
              type="text"
              className="expert-panel__input"
              maxLength={20}
              placeholder={t.expertPanelExtras.tagPlaceholderStyle}
              value={tag3}
              onChange={(e) => setTag3(e.target.value)}
            />
          </div>

          <button
            className="expert-panel__save button_primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t.expertPanel.saving : t.expertPanel.save}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
